/**
 * Viewing-key claim submitted at login.
 *
 * Legacy spending keys are SHA-256 of a derivation signature; recovered keys
 * are loaded directly. Neither the key nor its original signature may leave
 * the browser. Neither must the nullifying key: nullifiers are
 * Poseidon(nullifyingKey, tokenHash, leafIndex), so whoever holds it can
 * recompute every nullifier this account has ever published and will ever
 * publish, and link the account's whole spend history on chain. It is therefore
 * absent from the claim, from the digest the wallet signs, and from the
 * ownership proof.
 *
 * What is submitted is mpk, the Baby Jubjub public key, and an EdDSA-Poseidon
 * signature over the challenge under the secret behind that public key — the
 * same key the JoinSplit circuit requires to spend. That proves the caller
 * holds account authority rather than merely knowing a published address.
 */

import { deriveSpendingKeys, getSigningKey } from "./keys";
import type { SpendingKeys } from "./types";

const BN254_SCALAR_FIELD =
  21888242871839275222246405745257275088548364400416034343698204186575808495617n;

export interface ViewingKeyClaim {
  mpk: string;
  publicKey: [string, string];
}

export interface AccountOwnershipProof {
  R8: [string, string];
  S: string;
}

export interface AccountLoginClaim {
  claim: ViewingKeyClaim;
  proof: AccountOwnershipProof;
}

function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  return bytes.buffer.slice(
    bytes.byteOffset,
    bytes.byteOffset + bytes.byteLength,
  ) as ArrayBuffer;
}

async function sha256Hex(preimage: string): Promise<string> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(preimage),
  );
  return Array.from(new Uint8Array(digest))
    .map((byte) => byte.toString(16).padStart(2, "0"))
    .join("");
}

export function toViewingKeyClaim(spendingKeys: SpendingKeys): ViewingKeyClaim {
  return {
    mpk: `0x${spendingKeys.mpk.toString(16).padStart(64, "0")}`,
    publicKey: [
      spendingKeys.publicKey[0].toString(),
      spendingKeys.publicKey[1].toString(),
    ],
  };
}

/**
 * Must stay byte-identical to accountOwnershipMessageField in
 * api/_lib/account-proof.js, or every login fails proof verification.
 */
export async function accountOwnershipMessageField(
  wallet: string,
  nonce: string,
  claim: ViewingKeyClaim,
): Promise<bigint> {
  const digest = await sha256Hex(
    `stealthos:account-ownership:v2:${wallet}:${nonce}:${claim.mpk}:${claim.publicKey[0]}:${claim.publicKey[1]}`,
  );
  return BigInt(`0x${digest}`) % BN254_SCALAR_FIELD;
}

async function signAccountOwnership(
  spendingKeys: SpendingKeys,
  wallet: string,
  nonce: string,
  claim: ViewingKeyClaim,
): Promise<AccountOwnershipProof> {
  const circomlibjs = (await import("circomlibjs")) as unknown as {
    buildEddsa: () => Promise<any>;
  };
  const eddsa = await circomlibjs.buildEddsa();
  const signature = eddsa.signPoseidon(
    await getSigningKey(spendingKeys.spendingKey),
    eddsa.F.e(await accountOwnershipMessageField(wallet, nonce, claim)),
  );
  return {
    R8: [
      eddsa.F.toObject(signature.R8[0]).toString(),
      eddsa.F.toObject(signature.R8[1]).toString(),
    ],
    S: signature.S.toString(),
  };
}

/**
 * Derives the login claim from a wallet derivation signature.
 *
 * The seed this computes is the spending key, so it is confined to this
 * function: callers receive only the viewing key and a per-nonce signature over
 * it. This wrapper preserves legacy signature derivation; active wallets must
 * use buildAccountLoginClaimFromSpendingKeys so recovery does not change identity.
 */
export async function buildAccountLoginClaim(
  signatureBytes: Uint8Array,
  wallet: string,
  nonce: string,
): Promise<AccountLoginClaim> {
  const digest = await crypto.subtle.digest(
    "SHA-256",
    toArrayBuffer(signatureBytes),
  );
  const spendingKeys = await deriveSpendingKeys(new Uint8Array(digest));
  return buildAccountLoginClaimFromSpendingKeys(spendingKeys, wallet, nonce);
}

/** Prove the active private account, including one restored independently of
 * the public wallet's latest setup signature. Secret key material stays local. */
export async function buildAccountLoginClaimFromSpendingKeys(
  spendingKeys: SpendingKeys,
  wallet: string,
  nonce: string,
): Promise<AccountLoginClaim> {
  const claim = toViewingKeyClaim(spendingKeys);
  return {
    claim,
    proof: await signAccountOwnership(spendingKeys, wallet, nonce, claim),
  };
}

/**
 * Must stay byte-identical to computeViewingKeyDigest in api/_lib/auth.js.
 * Any divergence makes every login fail signature verification.
 */
export async function computeViewingKeyDigest(
  claim: ViewingKeyClaim,
): Promise<string> {
  return sha256Hex(
    `stealthos:viewing-key:v3:${claim.mpk}:${claim.publicKey[0]}:${claim.publicKey[1]}`,
  );
}

export function challengeMessageWithViewingKey(
  baseMessage: string,
  viewingKeyDigest: string,
): string {
  return `${baseMessage}\nViewing key: ${viewingKeyDigest}`;
}
